import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import '../styles/Auth.css';

const EditProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    bio: user?.bio || '',
    username: user?.username || '',
  });
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(
    user?.profilePicture ? `http://localhost:5000${user.profilePicture}` : null
  );
  const [loading, setLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatar) {
      setError('Please select an avatar');
      return;
    }

    setAvatarLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const formDataFile = new FormData();
      formDataFile.append('avatar', avatar);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/${user._id}/avatar`,
        formDataFile,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setSuccess('Avatar uploaded successfully!');
      setAvatarPreview(`http://localhost:5000${response.data.user.profilePicture}`);
      setAvatar(null);
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error uploading avatar');
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/auth/profile/${user._id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Profile updated successfully!');
      setTimeout(() => navigate(`/profile/${user._id}`), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{t('profile.editProfile')}</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div style={{color: '#1dd1a1', background: 'rgba(29, 209, 161, 0.1)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem'}}>{success}</div>}
        
        {/* Avatar Upload Section */}
        <div style={{marginBottom: '1.5rem', textAlign: 'center'}}>
          <div style={{width: '100px', height: '100px', margin: '0 auto 1rem', borderRadius: '50%', overflow: 'hidden', border: '3px solid #1dd1a1', background: '#252525', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar preview" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
            ) : (
              <span style={{color: '#666', fontSize: '2rem'}}>📷</span>
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            id="avatar-input"
            style={{display: 'none'}}
          />
          <label htmlFor="avatar-input" style={{background: '#1dd1a1', color: 'black', padding: '0.5rem 1rem', borderRadius: '20px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold', marginRight: '0.5rem'}}>
            {t('profile.avatar')}
          </label>
          {avatar && (
            <button
              type="button"
              onClick={handleAvatarUpload}
              disabled={avatarLoading}
              style={{background: '#00b894', color: 'black', padding: '0.5rem 1rem', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold'}}
            >
              {avatarLoading ? 'Uploading...' : 'Upload Avatar'}
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            name="username"
            placeholder="Username"
            value={formData.username}
            onChange={handleChange}
            required
          />
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
          <textarea
            name="bio"
            placeholder="Bio"
            value={formData.bio}
            onChange={handleChange}
            style={{minHeight: '100px', width: '100%', padding: '0.75rem', border: '1px solid #2a2a2a', borderRadius: '8px', background: '#252525', color: 'white', marginBottom: '1rem', fontFamily: 'inherit'}}
          />
          <button className="submit-btn" type="submit" disabled={loading}>
            {loading ? 'Saving...' : t('profile.saveChanges')}
          </button>
          <button 
            className="submit-btn" 
            type="button" 
            onClick={() => navigate(`/profile/${user._id}`)}
            style={{background: '#666', marginTop: '0.5rem'}}
          >
            {t('profile.cancel')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;
